import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { publicPreviewUrl } from "../utils/publicUrl";
import {
  ArrowLeft, FlaskConical, Code2, FileText,
  Phone, Mail, MapPin, Star, Globe,
  ExternalLink, CheckCircle2, Loader2, User,
  Sparkles, Building2, UserCheck, Tag, RotateCcw, Pencil, X, Save, Users, Eye,
  Trash2,
} from "lucide-react";
import { authFetch, API } from "../utils/api";
import EmailSequencePanel from "../components/EmailSequencePanel";
import { InfoRow, Section, ActionButton } from "./BusinessDetail.UI";
import { ResearchResult, WebScriptResult, SiteResult } from "./BusinessDetail.Results";
import PromptPreviewModal from "../components/PromptPreviewModal";
import BusinessCRMFields from "../components/BusinessCRMFields";

const STATUS_LABELS = {
  new: "Nuevo", researched: "Investigado",
  emailed: "Seguimiento", site_generated: "Sitio Listo",
  generating_site: "Generando...", site_error: "Error en sitio",
  cancelled: "Cancelado",
  paid: "Pagado", won: "Ganado", lost: "Perdido"
};

const STATUS_COLORS = {
  new: "bg-gray-700 text-gray-200",
  researched: "bg-blue-900/60 text-blue-300",
  emailed: "bg-purple-900/60 text-purple-300",
  site_generated: "bg-amber-900/60 text-amber-300",
  generating_site: "bg-orange-900/60 text-orange-300",
  site_error: "bg-red-900/60 text-red-300",
  cancelled: "bg-slate-800/60 text-slate-300",
  paid: "bg-green-900/60 text-green-300",
  won: "bg-emerald-900/60 text-emerald-300",
  lost: "bg-red-900/60 text-red-300",
};

const EDITABLE_STATUSES = [
  { value: "new",            label: "Nuevo" },
  { value: "researched",     label: "Investigado" },
  { value: "emailed",        label: "Seguimiento" },
  { value: "site_generated", label: "Sitio Listo" },
  { value: "won",            label: "Ganado" },
  { value: "lost",           label: "Perdido" },
  { value: "paid",           label: "Pagado" },
];

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [research, setResearch] = useState(null);
  const [webScript, setWebScript] = useState(null);
  const [site, setSite] = useState(null);
  const [resettingCountdown, setResettingCountdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [loading, setLoading] = useState({ research: false, script: false, site: false });
  const [expanded, setExpanded] = useState({ research: true, script: false, site: false });
  const [slugOverride, setSlugOverride] = useState("");
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({});
  const [savingContact, setSavingContact] = useState(false);
  const [previewPromptType, setPreviewPromptType] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  // Memoized active users list to avoid filtering on every render
  const activeUsers = useMemo(() => users.filter(u => u.status === "active"), [users]);

  useEffect(() => {
    loadAll();
    authFetch(`${API}/users`).then(r => r?.json()).then(d => Array.isArray(d) && setUsers(d)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadAll captures `id` which is already listed
  }, [id]);

  // Clean up polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const loadAll = async () => {
    try {
      const [bRes, rRes, wsRes, sRes] = await Promise.all([
        authFetch(`${API}/businesses/${id}`),
        authFetch(`${API}/businesses/${id}/research`),
        authFetch(`${API}/businesses/${id}/web-script`),
        authFetch(`${API}/sites/by-business/${id}`),
      ]);
      if (!bRes) return;
      const b = await bRes.json();
      const r = rRes ? await rRes.json() : null;
      const ws = wsRes ? await wsRes.json() : null;
      const s = sRes ? await sRes.json() : null;
      setBiz(b);
      setResearch(r && r.summary ? r : null);
      setWebScript(ws && ws.hero_headline ? ws : null);
      setSite(s || null);
      // Resume polling if generation was already in progress
      if (b.status === "generating_site") startPolling();
    } catch {
      toast.error("Error al cargar datos");
    }
  };

  const startPolling = () => {
    if (pollRef.current) return; // already polling
    setLoading(l => ({ ...l, site: true }));
    let stuckCount = 0;
    let lastStep = "";
    pollRef.current = setInterval(async () => {
      try {
        const bRes = await authFetch(`${API}/businesses/${id}`);
        if (!bRes) return;
        const b = await bRes.json();
        setBiz(b);
        // Iter96 — tracking para auto-salir si nada cambia en 5 min
        const step = b.generation_step || "";
        if (step === lastStep) stuckCount += 1;
        else { stuckCount = 0; lastStep = step; }

        if (b.status === "site_generated") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Fetch the new site data
          const sRes = await authFetch(`${API}/sites/by-business/${id}`);
          const s = sRes ? await sRes.json() : null;
          setSite(s || null);
          setLoading(l => ({ ...l, site: false }));
          toast.success("¡Sitio web generado con éxito!");
        } else if (b.status === "site_error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLoading(l => ({ ...l, site: false }));
          toast.error(b.generation_error ? `Error: ${b.generation_error}` : "Error al generar el sitio. Intenta de nuevo.");
        } else if (b.status === "cancelled") {
          // Iter96 — antes el polling seguía infinito con status=cancelled
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLoading(l => ({ ...l, site: false }));
          toast.info("Generación cancelada.");
        } else if (stuckCount >= 60) {
          // Nada cambió en 5 min (60 × 5s) — salir del polling
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLoading(l => ({ ...l, site: false }));
          toast.warning("La generación parece estancada. Usá 'Cancelar generación' para liberar el estado.");
        }
      } catch { /* keep polling — network hiccup, no need to abort */ }
    }, 5000);
  };

  const doResetCountdown = async () => {
    setResettingCountdown(true);
    try {
      const res = await authFetch(`${API}/businesses/${id}/reset-countdown`, { method: "POST" });
      if (!res) return;
      const d = await res.json();
      if (d.success) toast.success(`Cuenta regresiva, créditos y chat reiniciados${d.accounts_reset > 0 ? ` (${d.accounts_reset} cuenta cliente)` : ""}`);
      else toast.error(d.detail || "No se pudo reiniciar");
    } catch { toast.error("Error de conexión"); }
    finally { setResettingCountdown(false); }
  };

  const doResearch = async () => {
    setLoading(l => ({ ...l, research: true }));
    try {
      const res = await authFetch(`${API}/businesses/${id}/research`, { method: "POST" });
      if (!res?.ok) throw new Error();
      const data = await res.json();
      setResearch(data);
      setBiz(b => ({ ...b, status: "researched" }));
      setExpanded(e => ({ ...e, script: true }));
      toast.success("¡Investigación completada!");
    } catch {
      toast.error("Error en la investigación");
    } finally {
      setLoading(l => ({ ...l, research: false }));
    }
  };

  const doWebScript = async () => {
    setLoading(l => ({ ...l, script: true }));
    try {
      const res = await authFetch(`${API}/businesses/${id}/web-script`, { method: "POST" });
      if (!res?.ok) throw new Error();
      const data = await res.json();
      setWebScript(data);
      setExpanded(e => ({ ...e, site: true }));
      toast.success("¡Script persuasivo generado!");
    } catch {
      toast.error("Error generando el script");
    } finally {
      setLoading(l => ({ ...l, script: false }));
    }
  };

  const doGenerateSite = async () => {
    try {
      const res = await authFetch(`${API}/businesses/${id}/generate-site`, {
        method: "POST",
        body: JSON.stringify({ slug_override: slugOverride || null }),
      });
      if (!res?.ok) throw new Error();
      const data = await res.json();
      if (data.status === "generating") {
        setBiz(b => ({ ...b, status: "generating_site" }));
        setSite(null);
        toast.info("Generando sitio en segundo plano... (puede tardar 2-3 min)");
        startPolling();
      }
    } catch {
      toast.error("Error al iniciar la generación del sitio");
      setLoading(l => ({ ...l, site: false }));
    }
  };

  // Iter94 — Cancelar una generación que quedó colgada. Marca el negocio como
  // "cancelled" y agrega el costo IA ya consumido como egreso en Finanzas.
  const doCancelGeneration = async () => {
    if (!window.confirm("¿Cancelar la generación? El costo IA acumulado se registrará como egreso.")) return;
    setLoading(l => ({ ...l, cancel: true }));
    try {
      const res = await authFetch(`${API}/businesses/${id}/cancel-generation`, { method: "POST" });
      if (!res?.ok) throw new Error();
      const data = await res.json();
      const costMsg = data.cost_usd
        ? `Costo registrado: $${Number(data.cost_usd).toFixed(4)} USD (${data.calls || 0} llamadas).`
        : "No se detectó consumo IA.";
      toast.success(`Generación cancelada. ${costMsg}`);
      // Refetch business to update status
      const b = await authFetch(`${API}/businesses/${id}`).then(r => r.json());
      setBiz(b);
    } catch {
      toast.error("Error al cancelar la generación");
    } finally {
      setLoading(l => ({ ...l, cancel: false }));
    }
  };

  const changeStatus = async (newStatus) => {
    if (!newStatus || newStatus === biz.status) return;
    setChangingStatus(true);
    try {
      const res = await authFetch(`${API}/businesses/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (res?.ok) {
        setBiz(b => ({ ...b, status: newStatus }));
        toast.success(`Estado: ${STATUS_LABELS[newStatus] || newStatus}`);
      } else toast.error("Error al cambiar estado");
    } catch { toast.error("Error de conexión"); }
    finally { setChangingStatus(false); }
  };

  const assignUser = async (userId) => {
    setAssigning(true);
    try {
      const res = await authFetch(`${API}/businesses/${id}/assign`, {
        method: "PATCH",
        body: JSON.stringify({ user_id: userId }),
      });
      const d = await res?.json();
      setBiz(prev => ({ ...prev, assigned_to: d?.assigned_to, assigned_name: d?.assigned_name }));
      toast.success(userId ? `Asignado a ${d?.assigned_name}` : "Asignación eliminada");
    } catch { toast.error("Error al asignar"); }
    finally { setAssigning(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  const startEditContact = () => {
    setContactDraft({
      lead_name:       biz.lead_name       || "",
      phone:           biz.phone           || "",
      email:           biz.email           || "",
      address:         biz.address         || "",
      estimated_value: biz.estimated_value != null ? String(biz.estimated_value) : "",
      category:        biz.category        || "",
    });
    setEditingContact(true);
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await authFetch(`${API}/businesses/${id}`, {
        method: "PATCH",
        body: JSON.stringify(contactDraft),
      });
      setBiz(prev => ({ ...prev, ...contactDraft }));
      setEditingContact(false);
      toast.success("Contacto actualizado");
    } catch {
      toast.error("Error al guardar contacto");
    } finally {
      setSavingContact(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar "${biz.name}" del pipeline? Esta acción es irreversible y también eliminará investigación, script y sitio generado.`)) return;
    setDeleting(true);
    try {
      const res = await authFetch(`${API}/businesses/${id}`, { method: "DELETE" });
      if (!res?.ok) throw new Error();
      toast.success("Empresa eliminada");
      navigate("/pipeline");
    } catch {
      toast.error("Error al eliminar");
      setDeleting(false);
    }
  };

  if (!biz) return (
    <div className="flex items-center justify-center min-h-screen bg-[#111111]">
      <Loader2 className="animate-spin text-gray-500" />
    </div>
  );

  const previewUrl = site
    ? publicPreviewUrl(site.slug || site.preview_url?.split('/pv/').pop())
    : null;
  // Admin preview URL skips the countdown trigger (?admin_preview=true)
  const adminPreviewUrl = previewUrl ? `${previewUrl}?admin_preview=true` : null;
  const clientToken = site?.client_token || null;

  // Generate initials from business name
  const initials = biz.name
    ? biz.name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase()
    : "B";

  return (
    <div className="min-h-screen bg-[#111111] pb-24">

      {/* ── Header ── */}
      <div className="relative overflow-hidden">
        {/* Subtle gradient accent */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#ffc700]/5 via-transparent to-[#0074b7]/5 pointer-events-none" />

        <div className="px-4 pt-5 pb-5">
          <div className="flex items-center justify-between mb-5 gap-3">
            <button
              onClick={() => navigate("/pipeline")}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              <ArrowLeft size={15} /> Volver al Pipeline
            </button>
            <button
              data-testid="delete-business-btn"
              onClick={handleDelete}
              disabled={deleting}
              title="Eliminar empresa"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              style={{ background: "rgba(239,68,68,0.08)", color: "#f87171", border: "1px solid rgba(239,68,68,0.20)" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.18)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
            >
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              <span className="hidden sm:inline">{deleting ? "Eliminando..." : "Eliminar"}</span>
            </button>
          </div>

          {/* Business card */}
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#ffc700]/20 to-[#FF4F00]/20 border border-white/10 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-black text-[#ffc700]">{initials}</span>
            </div>

            <div className="flex-1 min-w-0">
              <span className={`inline-flex items-center text-[10px] font-black px-2.5 py-0.5 rounded-full tracking-wide uppercase mb-2 ${STATUS_COLORS[biz.status] || "bg-gray-700 text-gray-300"}`}>
                {STATUS_LABELS[biz.status] || biz.status}
              </span>
              <h1 className="text-xl font-black text-white tracking-tight leading-tight mb-0.5">{biz.name}</h1>
              {biz.category && (
                <div className="flex items-center gap-1.5">
                  <Building2 size={11} className="text-gray-500 flex-shrink-0" />
                  <p className="text-sm text-gray-400">{biz.category}</p>
                </div>
              )}
            </div>
          </div>

          {/* Info grid */}
          <div className="mt-4 rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>

            {/* ── Pipeline fields (siempre visibles) ── */}
            <div className="px-4 py-3 grid grid-cols-2 gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: "#6b7280" }}>Estado</p>
                <span className={`inline-flex text-[10px] font-black px-2 py-0.5 rounded-full tracking-wide uppercase ${STATUS_COLORS[biz.status] || "bg-gray-700 text-gray-300"}`}>
                  {STATUS_LABELS[biz.status] || biz.status}
                </span>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: "#6b7280" }}>Valor Estimado</p>
                <p className="text-sm font-mono font-bold text-white">
                  {biz.estimated_value ? `$${Number(biz.estimated_value).toLocaleString("es-UY")}` : <span style={{ color: "#6b7280" }}>—</span>}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: "#6b7280" }}>Categoría</p>
                <p className="text-xs text-white">{biz.category || <span style={{ color: "#6b7280" }}>—</span>}</p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: "#6b7280" }}>Última Actividad</p>
                <p className="text-xs" style={{ color: "#adaaaa" }}>
                  {(() => {
                    const d = biz.updated_at || biz.last_contact || biz.created_at;
                    if (!d) return "—";
                    try {
                      const diff = Math.floor((Date.now() - new Date(d)) / 60000);
                      if (diff < 1) return "Ahora";
                      if (diff < 60) return `Hace ${diff} min`;
                      if (diff < 1440) return `Hace ${Math.floor(diff/60)}h`;
                      return `Hace ${Math.floor(diff/1440)}d`;
                    } catch { return d; }
                  })()}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: "#6b7280" }}>Captado</p>
                <p className="text-xs" style={{ color: "#adaaaa" }}>
                  {biz.created_at ? new Date(biz.created_at).toLocaleDateString("es-UY", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                </p>
              </div>
              {biz.source && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest mb-1" style={{ color: "#6b7280" }}>Fuente</p>
                  <p className="text-xs text-white capitalize">{biz.source}</p>
                </div>
              )}
            </div>

            {/* ── Contact + extra fields ── */}
            <div className="p-3.5 space-y-2">
              {!editingContact ? (
                <>
                  {biz.lead_name && <InfoRow icon={<User size={12} />} text={biz.lead_name} />}
                  {biz.phone    && <InfoRow icon={<Phone size={12} />} text={biz.phone} />}
                  {biz.email    && <InfoRow icon={<Mail size={12} />} text={biz.email} />}
                  {biz.address  && <InfoRow icon={<MapPin size={12} />} text={biz.address} />}
                  {biz.website && biz.website !== "https://N/A" && biz.website !== "N/A" && (
                    <InfoRow icon={<Globe size={12} />} text={biz.website} />
                  )}
                  {biz.rating && (
                    <div className="flex items-center gap-2">
                      <Star size={12} className="text-[#ffc700] fill-[#ffc700] flex-shrink-0" />
                      <span className="text-xs text-gray-300 font-medium">{biz.rating} <span className="text-gray-500">({biz.reviews || 0} reseñas)</span></span>
                    </div>
                  )}
                  {/* Edit + CRM buttons */}
                  <div className="flex items-center gap-2 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <button
                      data-testid="edit-contact-btn"
                      onClick={startEditContact}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#ffc700] transition-colors"
                    >
                      <Pencil size={11} /> Editar datos
                    </button>
                    {biz.crm_contact_id ? (
                      <a data-testid="view-in-crm-btn" href={`/crm/${biz.crm_contact_id}`}
                        className="flex items-center gap-1 text-xs text-[#0074b7] hover:text-blue-400 transition-colors ml-auto"
                      >
                        <Users size={11} /> Ver en CRM
                      </a>
                    ) : (
                      <button data-testid="search-in-crm-btn"
                        onClick={() => navigate("/crm", { state: { search: biz.name } })}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors ml-auto"
                      >
                        <Users size={11} /> Buscar en CRM
                      </button>
                    )}
                  </div>
                </>
              ) : (
                /* Edit mode */
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Editar datos</p>
                  {[
                    { key: "lead_name",       label: "Nombre del lead",      icon: <User size={11} />,    type: "text"   },
                    { key: "phone",           label: "Teléfono",              icon: <Phone size={11} />,   type: "text"   },
                    { key: "email",           label: "Email",                 icon: <Mail size={11} />,    type: "email"  },
                    { key: "address",         label: "Dirección",             icon: <MapPin size={11} />,  type: "text"   },
                    { key: "category",        label: "Categoría",             icon: <Tag size={11} />,     type: "text"   },
                    { key: "estimated_value", label: "Valor estimado (USD)", icon: <span className="text-[10px] font-bold text-gray-600">$</span>, type: "number" },
                  ].map(({ key, label, icon, type }) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-gray-600 flex-shrink-0">{icon}</span>
                      <input
                        data-testid={`contact-edit-${key}`}
                        type={type}
                        value={contactDraft[key] || ""}
                        onChange={e => setContactDraft(d => ({ ...d, [key]: e.target.value }))}
                        placeholder={label}
                        className="flex-1 bg-white/[0.06] border border-white/[0.12] rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#ffc700]/50 placeholder-gray-600"
                      />
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <button
                      data-testid="save-contact-btn"
                      onClick={saveContact}
                      disabled={savingContact}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#ffc700] text-black text-xs font-bold rounded-xl disabled:opacity-60"
                    >
                      {savingContact ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      {savingContact ? "Guardando..." : "Guardar"}
                    </button>
                    <button
                      data-testid="cancel-edit-contact-btn"
                      onClick={() => setEditingContact(false)}
                      className="px-3 py-2 bg-white/[0.07] text-gray-400 text-xs rounded-xl hover:bg-white/[0.12] transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <UserCheck size={12} className="text-gray-500 flex-shrink-0" />
                <span className="text-xs text-gray-500">Asignado a:</span>
                <select
                  data-testid="assign-user-select"
                  value={biz.assigned_to || ""}
                  onChange={e => assignUser(e.target.value)}
                  disabled={assigning}
                  className="flex-1 bg-transparent text-xs text-gray-300 border-none focus:outline-none cursor-pointer"
                >
                  <option value="" className="bg-[#1a1a1a]">Sin asignar</option>
                  <option value="admin" className="bg-[#1a1a1a]">Admin</option>
                  {activeUsers.map(u => (
                    <option key={u.id} value={u.id} className="bg-[#1a1a1a]">{u.name}</option>
                  ))}
                </select>
                {assigning && <Loader2 size={10} className="animate-spin text-gray-500" />}
              </div>
            </div>

            {/* Status selector */}
            <div className="pt-1 border-t border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Tag size={12} className="text-gray-500 flex-shrink-0" />
                <span className="text-xs text-gray-500">Estado:</span>
                <select
                  data-testid="business-status-select"
                  value={biz.status || ""}
                  onChange={e => changeStatus(e.target.value)}
                  disabled={changingStatus || biz.status === "generating_site"}
                  className="flex-1 bg-transparent text-xs text-gray-300 border-none focus:outline-none cursor-pointer"
                >
                  {EDITABLE_STATUSES.map(s => (
                    <option key={s.value} value={s.value} className="bg-[#1a1a1a]">{s.label}</option>
                  ))}
                </select>
                {changingStatus && <Loader2 size={10} className="animate-spin text-gray-500" />}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mx-4" />
      </div>

      <div className="px-4 mt-4 flex flex-col gap-3">

        {/* 1. Research */}
        <Section
          icon={<FlaskConical size={16} />}
          title="Investigación IA"
          subtitle={research ? "Completado" : "Analiza el negocio con IA"}
          done={!!research}
          expanded={expanded.research}
          onToggle={() => setExpanded(e => ({ ...e, research: !e.research }))}
        >
          {!research ? (
            <div className="flex flex-col gap-2">
              <ActionButton testId="research-btn" loading={loading.research} onClick={doResearch}
                label="Investigar con IA" icon={<FlaskConical size={15} />} />
              <button
                data-testid="preview-research-prompt-btn"
                onClick={() => setPreviewPromptType("research")}
                className="text-[11px] text-gray-500 hover:text-[#FF4F00] flex items-center gap-1 self-start transition-colors"
              >
                <Eye size={11} /> Ver prompt resuelto antes de ejecutar
              </button>
            </div>
          ) : (
            <ResearchResult data={research} />
          )}
          {research && (
            <div className="flex flex-col gap-2">
              <ActionButton testId="re-research-btn" loading={loading.research} onClick={doResearch}
                label="Re-investigar" icon={<FlaskConical size={15} />} variant="secondary" />
              <button
                data-testid="preview-research-prompt-btn-v2"
                onClick={() => setPreviewPromptType("research")}
                className="text-[11px] text-gray-500 hover:text-[#FF4F00] flex items-center gap-1 self-start transition-colors"
              >
                <Eye size={11} /> Ver prompt resuelto
              </button>
            </div>
          )}
        </Section>

        {/* 2. Script Persuasivo Web */}
        <Section
          icon={<FileText size={16} />}
          title="Script Persuasivo Web"
          subtitle={webScript ? "Generado" : "Copy de conversión para el sitio"}
          done={!!webScript}
          expanded={expanded.script}
          onToggle={() => setExpanded(e => ({ ...e, script: !e.script }))}
        >
          {!webScript ? (
            <ActionButton testId="web-script-btn" loading={loading.script} onClick={doWebScript}
              label="Generar Script con IA" icon={<FileText size={15} />}
              loadingLabel="Generando copy de conversión..." />
          ) : (
            <WebScriptResult data={webScript} onCopy={copyToClipboard}
              bizId={id} onUpdate={(fresh) => setWebScript(fresh)} />
          )}
          {webScript && (
            <ActionButton testId="re-script-btn" loading={loading.script} onClick={doWebScript}
              label="Re-generar script" icon={<FileText size={15} />} variant="secondary" />
          )}
        </Section>

        {/* 3. Site Generation */}
        <Section
          icon={<Code2 size={16} />}
          title="Sitio Web"
          subtitle={site ? `/pv/${site.slug || '...'}` : biz.status === "generating_site" ? "Generando en segundo plano..." : "Genera el sitio web"}
          done={!!site}
          expanded={expanded.site}
          onToggle={() => setExpanded(e => ({ ...e, site: !e.site }))}
        >
          {biz.status === "generating_site" ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 size={28} className="animate-spin text-[#FF4F00]" />
              <p className="text-sm font-medium text-gray-300">Generando sitio web con IA...</p>
              {biz.generation_step && (
                <div className="w-full max-w-sm" data-testid="generation-progress">
                  <GenerationSteps current={biz.generation_step} detail={biz.generation_step_detail} />
                </div>
              )}
              <p className="text-xs text-gray-500 text-center">Este proceso puede tardar 2-3 minutos.<br />Puedes cerrar esta ventana y volver más tarde.</p>
              <button
                onClick={doCancelGeneration}
                disabled={loading.cancel}
                data-testid="cancel-site-generation-btn"
                className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:brightness-110 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "rgba(239, 68, 68, 0.12)", color: "#fca5a5", border: "1px solid rgba(239, 68, 68, 0.3)" }}
              >
                {loading.cancel ? (
                  <><Loader2 size={12} className="animate-spin" /> Cancelando...</>
                ) : (
                  <>✕ Cancelar generación</>
                )}
              </button>
              <p className="text-[10px] text-gray-600 text-center max-w-[280px] leading-relaxed">
                Registra el costo IA acumulado como egreso en Finanzas y libera el estado para reintentar.
              </p>
            </div>
          ) : biz.status === "site_error" && biz.generation_error ? (
            <div className="rounded-xl px-3 py-2.5 mb-2" data-testid="generation-error-banner"
                 style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
              <p className="text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">Última generación falló</p>
              <p className="text-[11px] text-red-200 font-mono break-all leading-snug">{biz.generation_error}</p>
              {!site && (
                <div className="flex flex-col gap-3 mt-3">
                  <ActionButton testId="generate-site-btn" loading={loading.site} onClick={doGenerateSite}
                    label="Reintentar generación" icon={<Code2 size={15} />}
                    loadingLabel="Iniciando generación..." />
                </div>
              )}
            </div>
          ) : !site ? (
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs text-gray-400 font-medium block mb-1">Slug personalizado (opcional)</label>
                <input
                  data-testid="slug-input"
                  value={slugOverride}
                  onChange={e => setSlugOverride(e.target.value)}
                  placeholder={`ej: ${biz.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="w-full border border-white/10 bg-white/5 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20"
                />
              </div>
              <ActionButton testId="generate-site-btn" loading={loading.site} onClick={doGenerateSite}
                label="Generar Sitio Web con IA" icon={<Code2 size={15} />}
                loadingLabel="Iniciando generación..." />
            </div>
          ) : (
            <SiteResult site={site} previewUrl={previewUrl} adminPreviewUrl={adminPreviewUrl} clientToken={clientToken} onCopy={copyToClipboard} onResetCountdown={doResetCountdown} resettingCountdown={resettingCountdown} />
          )}
          {site && biz.status !== "generating_site" && (
            <ActionButton testId="re-generate-site-btn" loading={loading.site} onClick={doGenerateSite}
              label="Re-generar sitio" icon={<Code2 size={15} />} variant="secondary"
              loadingLabel="Iniciando regeneración..." />
          )}
        </Section>

        {/* 3.5 CRM fields (tags, notes, follow-up, custom fields) — unified after migration 001 */}
        <BusinessCRMFields
          business={biz}
          onUpdate={(fresh) => setBiz(b => ({ ...b, ...fresh }))}
        />

        {/* 4. Email Sequence — visible always */}
        <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-xl p-4">
          <EmailSequencePanel
            businessId={id}
            businessEmail={biz.email}
            businessPhone={biz.phone}
            hasSite={!!site}
          />
        </div>
      </div>
      {previewPromptType && (
        <PromptPreviewModal
          businessId={id}
          promptType={previewPromptType}
          onClose={() => setPreviewPromptType(null)}
        />
      )}
    </div>
  );
}


// Iter96 — Progreso por paso durante la generación de sitio.
const GEN_STEPS = [
  { id: "loading_business",  label: "Cargando datos" },
  { id: "web_script",        label: "Generando copy persuasivo" },
  { id: "design",            label: "Diseñando layout (IA)" },
  { id: "design_parallel",   label: "Imagen hero + fotos en paralelo" },
  { id: "implementing_html", label: "Escribiendo HTML completo" },
  { id: "finalizing",        label: "Guardando sitio" },
];

function GenerationSteps({ current, detail }) {
  const resolveIdx = (c) => {
    if (!c) return 0;
    const exact = GEN_STEPS.findIndex(s => s.id === c);
    if (exact >= 0) return exact;
    if (c.startsWith("step1")) return 3;
    if (c.startsWith("step2")) return 4;
    if (c === "done")          return GEN_STEPS.length;
    return 0;
  };
  const currentIdx = resolveIdx(current);

  return (
    <ul className="space-y-1.5">
      {GEN_STEPS.map((s, i) => {
        const status = i < currentIdx ? "done" : i === currentIdx ? "active" : "pending";
        return (
          <li key={s.id} className="flex items-center gap-2 text-[11px]">
            <span
              className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0 ${
                status === "done"
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                  : status === "active"
                  ? "bg-orange-500/20 text-orange-300 border border-orange-500/40 animate-pulse"
                  : "bg-white/[0.04] text-gray-600 border border-white/[0.06]"
              }`}
            >
              {status === "done" ? "✓" : status === "active" ? "•" : ""}
            </span>
            <span className={`font-bold ${
              status === "done"   ? "text-emerald-300/80"
              : status === "active" ? "text-orange-200"
              : "text-gray-600"
            }`}>
              {s.label}
            </span>
            {status === "active" && detail && (
              <span className="text-[10px] text-gray-500 italic truncate">— {detail}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// sub-components moved to BusinessDetail.UI.js and BusinessDetail.Results.js
